import { useCallback } from 'react';
import { getLocale, shallowEqual, useIntl, useSelector } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import { getAgentConfig } from '../../ChatPage/utils';
import { getColumnLetter } from '../components/SmartTableGrid/utils';

/**
 * AI Excel 操作类型
 */
export enum AIExcelActionType {
  /** 智能填充列 */
  SMART_FILL_COLUMN = 'smart_fill_column',
  /** 智能填充行 */
  SMART_FILL_ROW = 'smart_fill_row',
  /** AI 润色表格 */
  AI_POLISH = 'ai_polish',
  /** 修改列数据条件 */
  MODIFY_COLUMN_CONDITION = 'modify_column_condition',
  /** 调整列数据格式 */
  ADJUST_COLUMN_FORMAT = 'adjust_column_format',
  /** 自定义输入 */
  CUSTOM_INPUT = 'custom_input',
}

/**
 * AI Excel 聊天来源类型
 */
export enum AIExcelChatSource {
  /** 聊天主入口 */
  MAIN_CHAT = 'main_chat',
  /** 右边 Excel 文件里的入口 */
  FILE_CHAT = 'file_chat',
  /** 智能行入口 */
  ROW_CHAT = 'row_chat',
  /** 智能列入口 */
  COLUMN_CHAT = 'column_chat',
}

/**
 * 发送参数类型
 */
export interface ISendParams {
  /** 列名称（智能填充列使用） */
  columnName?: string;
  /** 列索引（列操作使用） */
  colIndex?: number;
  /** 用户输入的 prompt */
  prompt: string;
}

/**
 * 操作类型配置
 */
interface IActionConfig {
  /** 来源 */
  from: AIExcelChatSource;
  /** i18n key，用于 formatMessage */
  i18nKey?: string;
  /** 是否直接使用 prompt（不需要模板拼接） */
  useRawPrompt?: boolean;
}

/**
 * 操作类型映射配置
 */
const ACTION_CONFIG_MAP: Record<AIExcelActionType, IActionConfig> = {
  [AIExcelActionType.SMART_FILL_COLUMN]: {
    from: AIExcelChatSource.COLUMN_CHAT,
    i18nKey: 'dba_smart_table_fill_column',
  },
  [AIExcelActionType.SMART_FILL_ROW]: {
    from: AIExcelChatSource.ROW_CHAT,
    i18nKey: 'dba_smart_table_fill_row',
  },
  [AIExcelActionType.AI_POLISH]: {
    from: AIExcelChatSource.FILE_CHAT,
    useRawPrompt: true,
  },
  [AIExcelActionType.MODIFY_COLUMN_CONDITION]: {
    from: AIExcelChatSource.COLUMN_CHAT,
    i18nKey: 'dba_smart_table_modify_column_condition',
  },
  [AIExcelActionType.ADJUST_COLUMN_FORMAT]: {
    from: AIExcelChatSource.COLUMN_CHAT,
    i18nKey: 'dba_smart_table_adjust_column_format',
  },
  [AIExcelActionType.CUSTOM_INPUT]: {
    from: AIExcelChatSource.MAIN_CHAT,
    useRawPrompt: true,
  },
};

/**
 * startNewConversation 方法类型定义
 */
export type TStartNewConversation = (
  queryValue: string,
  singleMessageFileList: any,
  options?: {
    onOpen?: () => void;
    onSuccess?: () => void;
    onError?: () => void;
    onComplete?: () => void;
    onBefore?: (...args: any) => void;
  },
  otherParams?: {
    from?: string;
    outConversationsList?: any[];
    logFrom?: string;
    serverParams?: any;
    customQuestion?: boolean;
    logParams?: Record<string, any>;
    fileListData?: any[];
  }
) => Promise<void>;

/**
 * Hook 配置参数
 */
export interface IUseSmartTableChatConfig {
  /** startNewConversation 方法 */
  startNewConversation: TStartNewConversation;
  /** 表格名称 */
  tableName?: string;
  /** 工作表名称 */
  sheetName?: string;
  /** 当前文件的 COS 路径（非 main_chat 来源时作为 args.input_excel 传递） */
  currentFileCosUrl?: string;
}

/**
 * 回调配置
 */
export interface ISendOptions {
  onSuccess?: () => void;
  onError?: () => void;
  onComplete?: () => void;
  onBefore?: () => void;
}

/**
 * 智能表格聊天 Hook
 * 统一封装 startNewConversation 的调用方式
 *
 * @example
 * const { sendSmartTableAction } = useSmartTableChat(config);
 *
 * // 智能填充列
 * sendSmartTableAction(AIExcelActionType.SMART_FILL_COLUMN, { columnName: '新列', prompt: '基于DAU计算' });
 *
 * // 智能填充行
 * sendSmartTableAction(AIExcelActionType.SMART_FILL_ROW, { prompt: '日期为2023-10-21' });
 *
 * // AI 润色
 * sendSmartTableAction(AIExcelActionType.AI_POLISH, { prompt: '优化表格格式' });
 *
 * // 修改列数据条件
 * sendSmartTableAction(AIExcelActionType.MODIFY_COLUMN_CONDITION, { columnName: 'DAU', prompt: '筛选条件' });
 *
 * // 调整列数据格式
 * sendSmartTableAction(AIExcelActionType.ADJUST_COLUMN_FORMAT, { columnName: 'DAU', prompt: '百分比格式' });
 */
export const useSmartTableChat = (config: IUseSmartTableChatConfig) => {
  const {
    startNewConversation,
    tableName = '',
    sheetName = '',
    currentFileCosUrl = '',
  } = config;
  const userInfo = useSelector(
    (state: any) => state.home.userInfo,
    shallowEqual,
  );
  const { formatMessage, locale } = useIntl();

  /**
   * 发送智能表格操作
   * @param type 操作类型
   * @param params 参数
   * @param options 回调配置
   */
  const sendSmartTableAction = useCallback(
    async (
      type: AIExcelActionType,
      params: ISendParams,
      options?: ISendOptions
    ) => {
      // 从 Map 中获取配置
      const actionConfig = ACTION_CONFIG_MAP[type];
      if (!actionConfig) {
        console.warn(`[useSmartTableChat] 未知的操作类型: ${type}`);
        return;
      }

      const { from, i18nKey, useRawPrompt } = actionConfig;

      // 生成 prompt
      let queryMessage: string;
      if (useRawPrompt) {
        // 直接使用原始 prompt（AI 润色、自定义输入等场景）
        queryMessage = params.prompt;
      } else if (i18nKey) {
        // 使用 formatMessage 生成国际化 prompt
        queryMessage = formatMessage(
          { id: i18nKey },
          { columnName: params.columnName || '', prompt: params.prompt }
        );
      } else {
        console.warn('[useSmartTableChat] 缺少 i18nKey 或 useRawPrompt 配置');
        return;
      }
      if (!queryMessage) {
        console.warn('[useSmartTableChat] 生成的 prompt 为空');
        return;
      }

      // 构建服务端参数（非 main_chat 来源时，传递 input_excel 参数）
      const functionParams: Record<string, any> = {
        name: 'ai_excel',
        source: from,
      };
      if (from !== AIExcelChatSource.MAIN_CHAT && currentFileCosUrl) {
        functionParams.args = {
          input_excel: [currentFileCosUrl],
        };
      }
      // 获取 Agent 配置信息
      const { selectedAgentId, currentMode, chatEngine } = getAgentConfig({
        userId: userInfo?.openid,
      });

      // 构建 frontend_metadata.field_name
      const isEN = getLocale() === LANGUAGE_EN;
      const colLetterTypes = [AIExcelActionType.MODIFY_COLUMN_CONDITION, AIExcelActionType.ADJUST_COLUMN_FORMAT];
      const getFieldName = () => {
        if (type === AIExcelActionType.AI_POLISH) {
          // AI 润色：只显示表名-sheet名，不显示用户输入的提问
          return tableName;
        }
        if (colLetterTypes.includes(type)) {
          const colLetter = params.colIndex !== undefined && params.colIndex >= 0
            ? getColumnLetter(params.colIndex) : '';
          const colSuffix = colLetter
            ? (isEN ? ` Column ${colLetter}: ${colLetter} Column` : `的${colLetter}: ${colLetter}列`)
            : '';
          return `${tableName}${colSuffix}`;
        }
        return tableName;
      };
      const fieldName = getFieldName();
      
      const serverParams = {
        model_id: selectedAgentId,
        mode: currentMode,
        ...(chatEngine && { chat_engine: chatEngine }),
        system_language: locale,
        function: functionParams,
        ...(fieldName && { frontend_metadata: { field_name: fieldName } }),
      };

      // 构建日志参数
      const logParams = {
        dataSource: 'agent',
        chatSource: from,
        tableName,
        sheetName,
        name: 'ai_excel',
      };
      // 调用 startNewConversation
      await startNewConversation(queryMessage, [], options, {
        from: 'send',
        serverParams,
        logParams,
      });
    },
    [startNewConversation, locale, tableName, sheetName, formatMessage, currentFileCosUrl]
  );

  return {
    /** 发送智能表格操作 */
    sendSmartTableAction,
  };
};

export default useSmartTableChat;
